import { onSnapshot } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { DashboardView } from '../components/app-detail/DashboardView'
import { SettingsView } from '../components/app-detail/SettingsView'
import { ActivityLogView } from '../components/app-detail/ActivityLogView'
import { CustomersView } from '../components/app-detail/CustomersView'
import { PaywallSettingsView } from './PaywallSettingsView'

import { useAuth } from '../auth/AuthProvider'
import {
  portalGetConfig,
  portalListConfigVersions,
  portalPublishConfig,
  portalRollbackConfig,
  portalValidateConfig,
  saasUpdateAppConfig,
} from '../lib/portalApi'
import { userAppRef, type PortalApp } from '../lib/portalData'

export function AppDetailPage() {
  const { appId = '' } = useParams()
  const { user } = useAuth()
  const uid = user?.uid ?? ''

  const [app, setApp] = useState<PortalApp | null>(null)
  const [name, setName] = useState('')
  const [bundleId, setBundleId] = useState('')
  const [packageName, setPackageName] = useState('')
  const [activeTab, setActiveTab] = useState<'dashboard' | 'overview' | 'paywall' | 'settings' | 'activity' | 'customers'>('dashboard')

  const [selectedEnv, setSelectedEnv] = useState('prod')

  const [configText, setConfigText] = useState(
    JSON.stringify({ schemaVersion: 1, placements: { home: { paywallId: 'default' } } }, null, 2),
  )

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const [latestConfig, setLatestConfig] = useState<unknown | null>(null)
  const [versions, setVersions] = useState<
    Array<{ version: number; createdAt: any; configHash: string }>
  >([])

  const [serverValidation, setServerValidation] = useState<string | null>(null)

  // Backend CHỈ lưu `apiKeyMasked` + `apiKeyHash`; khoá đầy đủ chỉ trả về đúng
  // một lần lúc tạo app. Trước đây ô này đọc `apiKey` — trường không bao giờ tồn
  // tại — nên luôn hiện "API key not found".
  const envKey = useMemo(() => {
    const envs = (app as any)?.envs
    if (!Array.isArray(envs)) return ''
    const rec = envs.find((e: any) => e?.env === selectedEnv)
    return typeof rec?.apiKeyMasked === 'string'
      ? rec.apiKeyMasked
      : typeof rec?.apiKey === 'string'
        ? rec.apiKey
        : ''
  }, [app, selectedEnv])

  useEffect(() => {
    if (!uid || !appId) return
    const ref = userAppRef(uid, appId)
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setApp(null)
          return
        }
        const data = snap.data() as any
        const next: PortalApp = {
          id: snap.id,
          name: String(data?.name ?? ''),
          bundleId: data?.bundleId,
          packageName: data?.packageName,
          envs: Array.isArray(data?.envs) ? data.envs : [],
          createdAt: data?.createdAt,
          updatedAt: data?.updatedAt,
        }
        setApp(next)
        setName(next.name)
        setBundleId(next.bundleId || '')
        setPackageName(next.packageName || '')
      },
      (e) => setError(e?.message ?? 'Failed to load app'),
    )
  }, [uid, appId])

  useEffect(() => {
    const envs = (app as any)?.envs
    if (!Array.isArray(envs) || !envs.length) return
    if (envs.some((e: any) => e?.env === selectedEnv)) return
    const first = envs[0]
    if (typeof first?.env === 'string') setSelectedEnv(first.env)
  }, [app, selectedEnv])

  useEffect(() => {
    ;(async () => {
      if (!uid || !appId) return
      try {
        const idToken = await user?.getIdToken()
        if (!idToken) return
        const resp = await portalListConfigVersions({
          tenantId: uid,
          appId,
          env: selectedEnv,
          idToken,
          limit: 25,
        })
        setVersions(resp.versions ?? [])
      } catch {
        setVersions([])
      }
    })()
  }, [uid, appId, selectedEnv, user])

  return (
    <>
      <div className="flex items-center gap-sm md:gap-md mb-lg md:mb-xl min-w-0">
        <Link to="/apps" className="text-on-surface-variant hover:text-primary transition-colors flex items-center justify-center p-2 rounded-full hover:bg-surface-container-high">
          <span className="material-symbols-outlined text-[24px]">arrow_back</span>
        </Link>
        <div className="min-w-0">
          <h2 className="font-headline-lg text-[22px] md:text-[32px] font-semibold text-on-surface leading-tight truncate">{app?.name || 'Loading...'}</h2>
          <p className="font-label-md text-[13px] md:text-[14px] text-on-surface-variant font-mono mt-1 truncate">ID: {appId}</p>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="flex gap-lg border-b border-outline-variant mb-lg md:mb-xl overflow-x-auto -mx-md px-md md:mx-0 md:px-0">
        {(['dashboard', 'overview', 'paywall', 'settings', 'activity', 'customers'] as const).map((tab) => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab)} 
            className={`pb-sm shrink-0 font-label-md text-[14px] transition-colors relative whitespace-nowrap ${activeTab === tab ? 'text-primary font-bold' : 'text-on-surface-variant hover:text-on-surface'}`}
          >
            {tab === 'dashboard' && 'Dashboard'}
            {tab === 'overview' && 'Remote Config'}
            {tab === 'paywall' && 'Paywall Designer'}
            {tab === 'settings' && 'Store Settings'}
            {tab === 'activity' && 'Activity Logs'}
            {tab === 'customers' && 'Customers'}
            {activeTab === tab && (
              <span className="absolute bottom-[-1px] left-0 w-full h-[2px] bg-primary rounded-t-full"></span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && uid && appId && selectedEnv && (
        <DashboardView appId={appId} tenantId={uid} env={selectedEnv} />
      )}
      {activeTab === 'settings' && uid && appId && <SettingsView appId={appId} tenantId={uid} packageName={(app as any)?.packageName} />}
      {activeTab === 'paywall' && uid && appId && <PaywallSettingsView appId={appId} tenantId={uid} />}
      {activeTab === 'activity' && uid && appId && <ActivityLogView appId={appId} tenantId={uid} />}
      {activeTab === 'customers' && uid && appId && <CustomersView appId={appId} tenantId={uid} />}

      {activeTab === 'overview' && (
        <div className="space-y-lg max-w-5xl">
          {error && <div className="p-md bg-error-container text-on-error-container rounded-lg border border-error/20 font-body-sm mb-md">{error}</div>}

          {/* General Information Card */}
          <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
            <h3 className="font-headline-md text-[20px] font-semibold text-on-surface mb-md">General Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-md mb-lg">
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">App Name</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
                  value={name} onChange={(e) => setName(e.target.value)} disabled={!app || busy} 
                />
              </div>
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Apple Bundle ID</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
                  value={bundleId} onChange={(e) => setBundleId(e.target.value)} disabled={!app || busy} 
                />
              </div>
              <div>
                <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Android Package Name</label>
                <input 
                  className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
                  value={packageName} onChange={(e) => setPackageName(e.target.value)} disabled={!app || busy} 
                />
              </div>
            </div>
            
            <div className="flex justify-end mb-lg">
              <button
                className="bg-primary text-on-primary font-label-md text-[14px] px-lg py-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-all shadow-sm"
                onClick={async () => {
                  if (!uid || !appId) return
                  setError(null)
                  setBusy(true)
                  try {
                    const idToken = await user?.getIdToken()
                    if (!idToken) throw new Error('Not authenticated')
                    await saasUpdateAppConfig({
                      idToken,
                      appId,
                      name: name !== app?.name ? name : undefined,
                      bundleId: bundleId !== (app?.bundleId || '') ? bundleId : undefined,
                      packageName: packageName !== (app?.packageName || '') ? packageName : undefined
                    })
                    setResult('App updated successfully!')
                  } catch (err: any) {
                    setError(err?.message ?? 'Failed to update app')
                  } finally {
                    setBusy(false)
                  }
                }}
                disabled={busy || (!name) || (name === app?.name && bundleId === (app?.bundleId || '') && packageName === (app?.packageName || ''))}
              >
                Save Changes
              </button>
            </div>

            {/* API Keys Section */}
            <div className="p-lg bg-surface-container-low rounded-xl border border-outline-variant/50">
              <div className="flex items-center gap-md mb-md">
                <strong className="font-label-md text-[14px] text-on-surface">Environment:</strong>
                <select
                  className="bg-surface-container-lowest border border-outline-variant text-on-surface px-3 py-1.5 rounded-lg font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  value={selectedEnv}
                  onChange={(e) => setSelectedEnv(e.target.value)}
                  disabled={busy}
                >
                  {(app?.envs ?? []).map((e: any) => (
                    <option key={String(e?.env)} value={String(e?.env)}>
                      {String(e?.env).toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="font-label-sm text-[12px] text-on-surface-variant mb-1">API Key ({selectedEnv})</div>
              {envKey ? (
                <div className="relative group">
                  <code className="block w-full p-3 bg-inverse-surface text-surface rounded-lg font-mono text-[13px] break-all">
                    {envKey}
                  </code>
                  <p className="font-body-sm text-[11px] text-on-surface-variant mt-2">
                    Khoá đầy đủ chỉ hiện một lần lúc tạo app và không lưu lại ở đâu.
                    Mất thì tạo app mới hoặc dùng Personal Access Token trong Account Settings.
                  </p>
                </div>
              ) : (
                <div className="text-on-surface-variant font-body-sm">
                  Môi trường này chưa có khoá nào.
                </div>
              )}
            </div>
          </div>

          {/* Remote Config Editor Card */}
          <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
            <div className="flex justify-between items-center mb-lg">
              <div>
                <h2 className="font-headline-md text-[20px] font-semibold text-on-surface">Remote Config (JSON)</h2>
                <p className="font-body-sm text-[13px] text-on-surface-variant mt-1">Directly edit the Entitlements & Paywalls configuration.</p>
              </div>
            </div>

            <div className="mb-lg">
              <textarea
                className="w-full min-h-[300px] p-4 bg-[#1e1e1e] border border-outline-variant rounded-xl text-[#d4d4d4] font-mono text-[13px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={configText}
                onChange={(e) => setConfigText(e.target.value)}
                disabled={busy}
                spellCheck={false}
              />
            </div>

            <div className="flex gap-sm mb-md flex-wrap">
              <button
                className="px-lg py-sm font-label-md text-[14px] bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high rounded-lg transition-colors"
                disabled={busy}
                onClick={async () => {
                  setError(null)
                  setServerValidation(null)
                  setBusy(true)
                  try {
                    const idToken = await user?.getIdToken()
                    if (!idToken) throw new Error('Not authenticated')
                    const cfg = JSON.parse(configText)
                    const v = await portalValidateConfig({ tenantId: uid, appId, env: selectedEnv, idToken, config: cfg })
                    if (v.valid) setServerValidation('Server validate: Passed ✅')
                    else setServerValidation(`Server validate: ERROR ❌\n` + v.errors.map((e: any) => `${e.instancePath || '/'}: ${e.message}`).join('\n'))
                  } catch (err: any) {
                    setError(err?.message ?? 'Validate failed')
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                Validate Config
              </button>
              
              <button
                className="px-lg py-sm font-label-md text-[14px] bg-primary text-on-primary hover:opacity-90 rounded-lg shadow-sm transition-all disabled:opacity-50"
                disabled={busy}
                onClick={async () => {
                  setError(null)
                  setResult(null)
                  setBusy(true)
                  try {
                    const idToken = await user?.getIdToken()
                    if (!idToken) throw new Error('Not authenticated')
                    const config = JSON.parse(configText)
                    const resp = await portalPublishConfig({ tenantId: uid, appId, env: selectedEnv, idToken, config })
                    setResult(`Successfully published version v${resp.version}`)
                    const vs = await portalListConfigVersions({ tenantId: uid, appId, env: selectedEnv, idToken, limit: 25 })
                    setVersions(vs.versions ?? [])
                  } catch (err: any) {
                    setError(err?.message ?? 'Publish failed')
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {busy ? 'Publishing...' : `Publish to ${selectedEnv.toUpperCase()}`}
              </button>

              <button
                className="px-lg py-sm font-label-md text-[14px] bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high rounded-lg transition-colors"
                disabled={busy}
                onClick={async () => {
                  setError(null)
                  setResult(null)
                  setBusy(true)
                  try {
                    const idToken = await user?.getIdToken()
                    if (!idToken) throw new Error('Not authenticated')
                    const resp = await portalGetConfig({ tenantId: uid, appId, env: selectedEnv, idToken })
                    setLatestConfig(resp.config)
                    setResult(`Loaded live version v${resp.version}`)
                  } catch (err: any) {
                    setError(err?.message ?? 'Fetch failed')
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                Load Live Config
              </button>
            </div>

            {serverValidation && (
              <pre className={`p-4 rounded-xl font-mono text-[13px] whitespace-pre-wrap mt-4 ${serverValidation.includes('ERROR') ? 'bg-error-container text-on-error-container' : 'bg-[#15803d]/10 text-[#15803d] border border-[#15803d]/20'}`}>
                {serverValidation}
              </pre>
            )}

            {result && (
              <div className="mt-4 p-4 bg-[#15803d]/10 border border-[#15803d]/20 text-[#15803d] rounded-xl font-body-sm">
                {result}
              </div>
            )}
          </div>

          {/* Live Config & Version History */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-lg">
            {latestConfig !== null && (
              <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
                <h3 className="font-headline-sm text-[18px] font-semibold text-on-surface mb-md">Currently Live</h3>
                <pre className="p-4 bg-inverse-surface text-surface rounded-xl overflow-auto font-mono text-[13px] h-[300px]">
                  {JSON.stringify(latestConfig, null, 2)}
                </pre>
              </div>
            )}

            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
              <h3 className="font-headline-sm text-[18px] font-semibold text-on-surface mb-md">Version History</h3>
              {versions.length ? (
                <div className="space-y-sm max-h-[300px] overflow-auto pr-2">
                  {versions.map((v) => (
                    <div key={v.version} className="p-md bg-surface-container-low border border-outline-variant rounded-lg flex items-center justify-between group">
                      <div className="min-w-0">
                        <div className="font-label-md text-[14px] font-semibold text-on-surface">Version v{v.version}</div>
                        <div className="font-mono text-[11px] text-on-surface-variant truncate">Hash: {v.configHash}</div>
                      </div>
                      <button
                        className="px-3 py-1 font-label-sm text-[12px] bg-surface-container-lowest border border-outline-variant rounded-md hover:bg-surface-container-high transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                        disabled={busy}
                        onClick={async () => {
                          setError(null)
                          setResult(null)
                          setBusy(true)
                          try {
                            const idToken = await user?.getIdToken()
                            if (!idToken) throw new Error('Not authenticated')
                            const rb = await portalRollbackConfig({ tenantId: uid, appId, env: selectedEnv, idToken, version: v.version })
                            setResult(`Rolled back to v${rb.version}`)
                            const latest = await portalGetConfig({ tenantId: uid, appId, env: selectedEnv, idToken })
                            setLatestConfig(latest.config)
                          } catch (err: any) {
                            setError(err?.message ?? 'Rollback failed')
                          } finally {
                            setBusy(false)
                          }
                        }}
                      >
                        Rollback
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-on-surface-variant font-body-sm h-32 flex items-center justify-center border-2 border-dashed border-outline-variant rounded-xl">
                  No versions published yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
